import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import supabase from './supabase/supabase';
import Login from './pages/Login';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import TestInterface from './pages/TestInterface';
import UpdatePassword from './pages/UpdatePassword';
import './App.css';

const AuthGuard = ({ children, user, profile, requiredRole }) => {
  if (!user) return <Navigate to="/login" replace />;
  
  if (profile?.status === 'banned') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4 font-inter">
        <div className="bg-white p-12 rounded-[3rem] shadow-xl border border-red-100 text-center max-w-md">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiIcons.FiShieldOff} className="text-4xl text-red-600" />
          </div>
          <h1 className="text-3xl font-black text-red-600 italic uppercase mb-4 tracking-tighter">Conta Bloqueada</h1>
          <p className="text-slate-500 font-bold text-sm uppercase leading-relaxed px-4">
            Sua conta foi suspensa por violar nossos termos de uso. Entre em contato com o suporte para mais informações.
          </p>
          <button 
            onClick={() => supabase.auth.signOut()} 
            className="mt-10 w-full py-5 bg-slate-900 text-white font-black rounded-[1.5rem] uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200"
          >
            Sair da Conta
          </button>
        </div>
      </div>
    );
  }

  if (requiredRole && profile?.role !== requiredRole) {
    return <Navigate to={profile?.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const syncCounter = useRef(0);

  const fetchProfile = async (userId, attempt = 1) => {
    try {
      const { data } = await supabase.from('app_profiles').select('*').eq('id', userId);
      if (data && data.length > 0) return data[0];
      
      if (attempt < 5) {
        await new Promise(r => setTimeout(r, 1500));
        return fetchProfile(userId, attempt + 1);
      }
      return null;
    } catch (err) {
      console.error("Erro ao buscar perfil:", err);
      return null;
    }
  };

  const syncAuth = async (session) => {
    const currentSync = ++syncCounter.current;
    
    if (!session?.user) {
      setUser(null);
      setProfile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const profileData = await fetchProfile(session.user.id);
    
    if (currentSync === syncCounter.current) {
      if (profileData) {
        setUser(session.user);
        setProfile(profileData);
      } else {
        // Fallback: Criar perfil se não existir
        try {
          const { data: newProfile } = await supabase.from('app_profiles').insert({
            id: session.user.id,
            email: session.user.email,
            full_name: session.user.user_metadata?.full_name || session.user.email,
            role: 'user',
            credits: 0
          }).select().single();
          
          setUser(session.user);
          setProfile(newProfile);
        } catch (err) {
          console.error("Falha no fallback de perfil:", err);
        }
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    // Inicialização
    supabase.auth.getSession().then(({ data: { session } }) => syncAuth(session));

    // Ouvinte de mudanças na autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
        setLoading(false);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        syncAuth(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await supabase.auth.signOut();
      // O listener onAuthStateChange cuidará de limpar o estado
    } catch (err) {
      console.error("Erro no logout:", err);
    } finally {
      setUser(null);
      setProfile(null);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="font-black text-slate-400 uppercase text-[10px] tracking-[0.3em] italic">Sincronizando Acesso...</p>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to={profile?.role === 'admin' ? '/admin' : '/dashboard'} replace />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/test/:id" element={<TestInterface />} />
        
        <Route path="/dashboard" element={
          <AuthGuard user={user} profile={profile} requiredRole="user">
            <UserDashboard user={profile} onLogout={handleLogout} />
          </AuthGuard>
        } />
        
        <Route path="/admin" element={
          <AuthGuard user={user} profile={profile} requiredRole="admin">
            <AdminDashboard user={profile} onLogout={handleLogout} />
          </AuthGuard>
        } />

        <Route path="/" element={<Navigate to={user ? (profile?.role === 'admin' ? '/admin' : '/dashboard') : '/login'} replace />} />
      </Routes>
    </Router>
  );
}

export default App;